/**
 * The feature flag catalog.
 *
 * Every flag the apps read is named here. A string literal typed at a
 * call site is what lets `feature-analytics-sesion-replay` compile and then
 * silently evaluate to "off" forever, because PostHog answers an unknown
 * key with `undefined` rather than an error.
 *
 * The FRONTEND imports these values directly. The BACKEND cannot — the
 * `@shared/types` alias is a tsconfig `paths` entry, `tsc` emits the bare
 * specifier and nothing rewrites it before `node dist/...` runs, so only
 * `import type` survives. It therefore repeats the keys in
 * `apps/backend/src/utils/feature-flags.ts`, typed against the
 * `FeatureFlagName` union below so a drifted key fails the build. Adding
 * a flag here means adding it there too if the backend reads it.
 *
 * ## Naming
 *
 * `feature-<area>-<flag>`
 *
 * - `feature-` — a fixed prefix. PostHog's key list is flat and also holds
 *   experiment keys and survey targeting keys; the prefix is what makes
 *   "flags this code reads" filterable in the dashboard.
 * - `<area>` — the product area, lowercase, matching a directory under
 *   `apps/frontend/src/features` or `apps/backend/src/modules` where one
 *   applies (`search`, `payments`, `realtor`). `analytics` and `platform`
 *   are the two cross-cutting areas.
 * - `<flag>` — kebab-case, naming the THING gated, not the change to it:
 *   `session-replay`, not `enable-session-replay` or `new-replay`. A flag
 *   outlives the rollout that introduced it, and "new" stops being true.
 *
 * Hyphens are the separator because PostHog rejects `.` in a flag key —
 * its API matches `^[a-zA-Z0-9_-]+$` — so a dotted key cannot be created
 * in the dashboard at all. A consequence is that hyphens now separate
 * every segment, so the segments are no longer recoverable from the key
 * alone; the catalog alias is what names each part. The pattern below
 * enforces the prefix, lowercase, and that an area and a flag are both
 * present.
 */

export const FEATURE_FLAGS = {
	/**
	 * Arms PostHog session replay. Off by default and fail-closed: replay
	 * additionally requires an authenticated user, a passing sample roll and
	 * no local opt-out. See `apps/frontend/src/lib/session-replay.ts`.
	 */
	sessionReplay: "feature-analytics-session-replay",
} as const;

/** Every flag key the apps may ask PostHog about. */
export type FeatureFlagName =
	(typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

/**
 * What PostHog can answer. `undefined` is a real and common answer — the
 * flag does not exist, the request failed, or flags have not loaded yet —
 * and is distinct from `false`, which is a definite "off". A caller that
 * collapses the two has picked a default; it should say which one.
 */
export type FeatureFlagValue = boolean | string | undefined;

/**
 * The value each flag takes when PostHog cannot answer: the integration is
 * disabled, the network call failed, or the key is not defined in the
 * project. This is the value the app ships with, so it must be the safe
 * side of each flag rather than the intended end state.
 */
export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlagName, boolean> = {
	[FEATURE_FLAGS.sessionReplay]: false,
};

/**
 * `feature-` then at least two lowercase segments (area and flag), used by
 * the catalog's own test.
 */
export const FEATURE_FLAG_KEY_PATTERN = /^feature(-[a-z][a-z0-9]*){2,}$/;
